import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Pencil, PlugZap, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { SearchInput } from "@/components/shared/search-input";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type ConstraintGroup,
  type ConstraintType,
  useConstraintTypes,
  useConstraintValues,
  useReorderConstraintType,
  useSetConstraintValueHidden,
  useUpdateConstraintType,
} from "@/hooks/use-constraints";
import {
  type ConstraintTypeUpdateInput,
  constraintTypeUpdateSchema,
} from "@/schemas/constraint-types";

/**
 * Settings → Config → Constraints — the groupings people are rated under, and
 * the values they are rated against.
 *
 * **The values are NOT Enable's and cannot be edited here.** They live in NOS
 * (`Industry`, `AccountType`, `Offering`, `Platform`), which already held all
 * four lists and keeps them current — Enable used to keep hand-maintained copies,
 * the same upkeep burden that killed the Skills list constraints replaced.
 *
 * Two things on this page ARE Enable's, and they are the whole reason it still
 * exists:
 *
 *  - the grouping LABEL, so we can say "Domain" where NOS says "Industry";
 *  - the HIDE switch, so a value nobody should be rated on drops out of the
 *    pickers without anybody touching NOS.
 *
 * Hiding never removes a rating. A value 22 people hold can be hidden: they keep
 * their ratings, those ratings keep counting on the coverage report, and the
 * value simply stops being offerable for new ones.
 *
 * This page absorbed the separate "Constraint types" page — three constraint
 * pages became two. The familiarity scale keeps its own page, because rewording
 * a rung is a different job from curating a vocabulary.
 */
export function SettingsConstraintsPage() {
  const types = useConstraintTypes();
  const values = useConstraintValues();
  const [search, setSearch] = useState("");
  const [hiddenOnly, setHiddenOnly] = useState(false);
  const [editing, setEditing] = useState<ConstraintType | null>(null);
  /**
   * Which grouping's values are showing on the right.
   *
   * **Master/detail is what this page's data needs.** NOS's Domain list alone is
   * 148 rows: laid out one under another, "Offering / Technology" began nearly
   * eight screens down and the groupings were present but unreachable. With the
   * four names in a fixed left column the whole vocabulary is legible at a
   * glance and only one list is ever long. Same shape as the Constraints report
   * and Capacity, which solved the same problem.
   */
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const groups = values.data?.groups ?? [];
  const typeRows = useMemo(() => types.data ?? [], [types.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.map((g) => ({
      ...g,
      values: g.values
        .filter((v) => {
          if (hiddenOnly && !v.hidden) return false;
          if (!q) return true;
          return v.name.toLowerCase().includes(q) || g.label.toLowerCase().includes(q);
        })
        // Hidden values sink to the bottom, keeping NOS's alphabetical order
        // within each half. This is the convention every other config list here
        // already follows (see `HideRetiredFilter`): what is out of use is
        // always a SUFFIX, never a gap between two live rows. Sorted here rather
        // than in `listConstraintValues` because this is the only surface that
        // shows hidden values at all — the person picker excludes them outright,
        // and re-ordering the shared payload would be a change nobody asked for.
        .sort((a, b) => Number(a.hidden) - Number(b.hidden)),
    }));
  }, [groups, search, hiddenOnly]);

  if (types.isError || values.isError) {
    return (
      <ErrorState
        message={types.error?.message ?? values.error?.message ?? "Failed to load constraints"}
        onRetry={() => {
          types.refetch();
          values.refetch();
        }}
      />
    );
  }

  const hiddenCount = groups.reduce((n, g) => n + g.values.filter((v) => v.hidden).length, 0);

  // Default to the first grouping in the admin's configured order — they own
  // that ordering on this very page, so it is the configurable answer rather
  // than a hard-coded favourite. Falls back if the selection disappears.
  const selected = filtered.find((g) => g.key === selectedKey) ?? filtered[0] ?? null;
  const selectedType = typeRows.find((t) => t.key === selected?.key);
  const selectedIndex = filtered.findIndex((g) => g.key === selected?.key);
  const filtering = search.trim().length > 0 || hiddenOnly;

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        What a person can be rated familiar with. The four groupings and their values come from{" "}
        <span className="font-medium text-foreground">NOS</span>, which is where they are kept
        current — nothing is added or renamed here. What you can change is what each grouping is{" "}
        <span className="font-medium text-foreground">called</span>, and whether a value is{" "}
        <span className="font-medium text-foreground">offered</span>. Hiding a value takes it out of
        the pickers without touching NOS, and leaves every rating already recorded against it
        exactly as it is.
      </p>

      {values.data?.connected === false ? (
        <EmptyState
          icon={PlugZap}
          title="Not connected to NOS"
          description="The constraint lists live in NOS, so they can't be shown until this environment has a NOS connection. The groupings below are still Enable's and can be renamed."
        />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search constraints…"
            className="min-w-[200px] max-w-sm flex-1"
          />
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="constraints-hidden-only"
              checked={hiddenOnly}
              onCheckedChange={(v) => setHiddenOnly(v === true)}
            />
            <label htmlFor="constraints-hidden-only" className="cursor-pointer select-none">
              Hidden only
            </label>
            {hiddenCount > 0 && (
              <span className="text-muted-foreground">({hiddenCount} hidden)</span>
            )}
          </div>
        </div>
      )}

      {values.isLoading || types.isLoading ? (
        <LoadingRows />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          {/* Master. Fixed at four rows, so it never scrolls and the whole
              vocabulary stays legible however long one list gets. */}
          {/* `self-start` — a grid item stretches to the row height by default,
              which framed the four rows in a box as tall as 148 values. */}
          <ul className="self-start overflow-hidden rounded-md border border-border">
            {filtered.map((group) => {
              const isSelected = group.key === selected?.key;
              const type = typeRows.find((t) => t.key === group.key);
              return (
                <li key={group.key}>
                  <button
                    type="button"
                    onClick={() => setSelectedKey(group.key)}
                    aria-current={isSelected}
                    className={`flex w-full flex-col items-start gap-0.5 border-border border-b px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-muted/50 ${
                      isSelected ? "bg-muted" : ""
                    }`}
                  >
                    <span className="flex w-full items-center gap-2">
                      <span className={`truncate text-sm ${isSelected ? "font-medium" : ""}`}>
                        {group.label}
                      </span>
                      {!group.isActive && <StatusPill tone="neutral" label="Retired" />}
                    </span>
                    {/* Counts follow the search, so searching inside one
                        grouping shows which OTHER one holds the hit — the same
                        reasoning as the report's tab counts. */}
                    <span className="text-muted-foreground text-xs">
                      {group.values.length} {filtering ? "matching" : ""}
                      {group.values.length === 1 ? " value" : " values"}
                      {type ? ` · ${type.ratingCount} rated` : ""}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Detail. */}
          {selected ? (
            <GroupPanel
              group={selected}
              type={selectedType}
              isFirst={selectedIndex === 0}
              isLast={selectedIndex === filtered.length - 1}
              filtering={filtering}
              onEdit={() => selectedType && setEditing(selectedType)}
            />
          ) : (
            <EmptyState
              icon={SlidersHorizontal}
              title="No groupings"
              description="The four constraint groupings are seeded on startup — if none are here, something is wrong with the seed."
            />
          )}
        </div>
      )}

      {editing && <ConstraintTypeDialog open onOpenChange={() => setEditing(null)} row={editing} />}
    </div>
  );
}

/**
 * The selected grouping: Enable's label and ordering in the header, NOS's values
 * beneath it. Values are read-only — the only thing writable about one is
 * whether Enable offers it.
 */
function GroupPanel({
  group,
  type,
  isFirst,
  isLast,
  filtering,
  onEdit,
}: {
  group: ConstraintGroup;
  type: ConstraintType | undefined;
  isFirst: boolean;
  isLast: boolean;
  filtering: boolean;
  onEdit: () => void;
}) {
  const reorder = useReorderConstraintType();
  const setHidden = useSetConstraintValueHidden();

  return (
    <section className="min-w-0 rounded-md border border-border">
      <header className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
        <h2 className="font-medium text-sm">{group.label}</h2>
        {!group.isActive && <StatusPill tone="neutral" label="Retired" />}
        <span className="text-muted-foreground text-xs">
          {group.values.length} {group.values.length === 1 ? "value" : "values"}
          {type ? ` · ${type.ratingCount} rated` : ""}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {/* Reorder moves this grouping in the LEFT column — and everywhere
              else the groupings are listed, which is the point of it. */}
          <ReorderButtons
            label={group.label}
            disableUp={isFirst || !type || reorder.isPending}
            disableDown={isLast || !type || reorder.isPending}
            onMove={(direction) => type && reorder.mutate({ id: type.id, direction })}
          />
          <Button variant="ghost" size="sm" onClick={onEdit} disabled={!type}>
            <Pencil className="mr-1 h-3.5 w-3.5" />
            Rename
          </Button>
        </div>
      </header>

      {group.description && (
        <p className="border-b border-border px-4 py-2 text-muted-foreground text-xs">
          {group.description}
        </p>
      )}

      {group.values.length === 0 ? (
        <p className="px-4 py-8 text-center text-muted-foreground text-sm">
          {filtering
            ? "Nothing in this grouping matches. Another one may hold it — the counts on the left say which."
            : "NOS has no active values under this grouping."}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {group.values.map((value) => (
            <li key={value.id} className="flex items-center gap-3 px-4 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={
                      value.hidden ? "text-muted-foreground text-sm line-through" : "text-sm"
                    }
                  >
                    {value.name}
                  </span>
                  {value.hidden && <StatusPill tone="neutral" label="Hidden" />}
                </div>
                {value.description && (
                  <p className="mt-0.5 text-muted-foreground text-xs">{value.description}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={setHidden.isPending}
                onClick={() =>
                  setHidden.mutate({
                    typeKey: group.key,
                    valueId: value.id,
                    hidden: !value.hidden,
                  })
                }
              >
                {value.hidden ? (
                  <>
                    <Eye className="mr-1 h-3.5 w-3.5" />
                    Show
                  </>
                ) : (
                  <>
                    <EyeOff className="mr-1 h-3.5 w-3.5" />
                    Hide
                  </>
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Renaming a grouping. There is no create and no delete: each grouping is bound
 * to one NOS table its values are read from, so a fifth would have nothing to
 * read, and deleting one would strand the ratings recorded under it.
 */
function ConstraintTypeDialog({
  open,
  onOpenChange,
  row,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ConstraintType;
}) {
  const update = useUpdateConstraintType(row.id);
  const form = useForm<ConstraintTypeUpdateInput>({
    resolver: zodResolver(constraintTypeUpdateSchema),
    defaultValues: {
      name: row.name,
      description: row.description ?? "",
      isActive: row.isActive,
    },
  });

  async function onSubmit(values: ConstraintTypeUpdateInput) {
    // `key` is never sent — it is immutable, it is what the startup seed matches
    // on, and it is what binds this grouping to its NOS list.
    await update.mutateAsync(values);
    onOpenChange(false);
  }

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={<>Rename "{row.name}"</>}
      description={
        <>
          What this grouping is called in Enable. NOS calls it something else and keeps its own name
          — changing this does not touch NOS.
        </>
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel="Save"
      pending={update.isPending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Domain" {...field} value={field.value ?? ""} />
            </FormControl>
            <FormDescription>
              The source list in NOS is <span className="font-medium">{row.key}</span>.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                rows={2}
                placeholder="What kind of thing belongs under this grouping?"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="isActive"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value ?? true} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Available for selection</FormLabel>
              <FormDescription>
                Turn this off to retire the grouping. It disappears from the pickers but keeps
                rendering on anyone already rated under it.
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
